import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from './Toast';
import { STAGES, STAGE_COLORS, INSURERS, stageProgress, timeAgo, getClaimWarnings, specialStageTheme } from '../lib/constants';
import PartsTab from './PartsTab';
import TagsTab from './TagsTab';
import InvoiceTab from './InvoiceTab';
import TasksTab from './TasksTab';
import { IcoTow, IcoRental, IcoUpload, IcoFile, IcoFolder } from './Icons';

const IS_MOBILE = () => window.innerWidth < 768;

export default function ClaimModal({ claimId, session, room, onClose, onDeleted, initialClaim, roomName }) {
  const [claim, setClaim] = useState(initialClaim || null);
  const [activeTab, setActiveTab] = useState('info');
  const [loading, setLoading] = useState(!initialClaim);
  const [updates, setUpdates] = useState([]);
  const [files, setFiles] = useState([]);
  const [rental, setRental] = useState(null);
  const [newUpdate, setNewUpdate] = useState('');
  const [addingUpdate, setAddingUpdate] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [showDelete, setShowDelete] = useState(false);
  const [stageOpen, setStageOpen] = useState(false);
  const [emailThreads, setEmailThreads] = useState([]);
  const [expandedThreadId, setExpandedThreadId] = useState(null);

  const mobile = IS_MOBILE();
  const BUCKET = 'claim-files';

  const loadClaim = useCallback(async () => {
    if (initialClaim) { setEditForm(initialClaim); return; }
    const { data, error } = await supabase
      .from('claims').select('*, tags(*), parts(*)')
      .eq('id', claimId).single();
    if (data) { setClaim(data); setEditForm(data); }
    if (error) console.error('loadClaim:', error);
    setLoading(false);
  }, [claimId]);

  const loadUpdates = useCallback(async () => {
    const [{ data: upd }, { data: logs }] = await Promise.all([
      supabase.from('claim_updates').select('*').eq('claim_id', claimId).order('created_at', { ascending: false }),
      supabase.from('activity_log').select('*').eq('claim_id', claimId).order('created_at', { ascending: false }),
    ]);
    const combined = [
      ...(upd || []).map(u => ({ ...u, _type: 'update' })),
      ...(logs || []).map(l => ({ ...l, _type: 'log' })),
    ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    setUpdates(combined);
  }, [claimId]);

  const loadFiles = useCallback(async () => {
    const { data } = await supabase.storage
      .from(BUCKET)
      .list(`${room.id}/${claimId}`, { sortBy: { column: 'created_at', order: 'desc' } });
    if (data && data.length > 0) {
      const withUrls = await Promise.all(data.slice(0, 6).map(async f => {
        const path = `${room.id}/${claimId}/${f.name}`;
        const { data: urlData } = await supabase.storage.from(BUCKET).createSignedUrl(path, 3600);
        return { ...f, url: urlData?.signedUrl, path };
      }));
      setFiles(withUrls);
    }
  }, [claimId, room.id]);

  const loadRental = useCallback(async () => {
    const { data } = await supabase.from('rental_invoices').select('*').eq('claim_id', claimId).single();
    if (data) setRental(data);
  }, [claimId]);

  const loadEmailThreads = useCallback(async () => {
    const { data } = await supabase.from('email_threads')
      .select('*, email_messages(id, sender, preview_text, received_at)')
      .eq('claim_id', claimId)
      .order('last_message_at', { ascending: false })
      .order('received_at', { ascending: true, foreignTable: 'email_messages' });
    if (data) setEmailThreads(data);
  }, [claimId]);

  // Prevent background scroll on iOS while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    loadClaim();
    loadUpdates();
    loadFiles();
    loadRental();
    loadEmailThreads();
    const ch = supabase.channel(`claim-${claimId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'claim_updates', filter: `claim_id=eq.${claimId}` }, loadUpdates)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'activity_log', filter: `claim_id=eq.${claimId}` }, loadUpdates)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'claims', filter: `id=eq.${claimId}` }, loadClaim)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'parts' }, loadClaim)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tags' }, loadClaim)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'email_threads', filter: `claim_id=eq.${claimId}` }, loadEmailThreads)
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [claimId, loadClaim, loadUpdates, loadFiles, loadRental, loadEmailThreads]);

  // Optimistic-UI helper: mutate the local claim immediately; the write runs in
  // the background and the realtime refetch reconciles the true state later.
  const mutateClaim = useCallback((fn) => setClaim(c => (c ? fn(c) : c)), []);

  async function addUpdate(e) {
    e.preventDefault();
    const text = newUpdate.trim();
    if (!text) return;
    const userName = session.user.user_metadata?.full_name || session.user.email;
    // Optimistic: show the update in the list and clear the input immediately
    const temp = { id: `temp-${Date.now()}`, claim_id: claimId, user_id: session.user.id, user_name: userName, text, created_at: new Date().toISOString(), _type: 'update' };
    setUpdates(u => [temp, ...u]);
    setNewUpdate('');
    const { error } = await supabase.from('claim_updates').insert({ claim_id: claimId, user_id: session.user.id, user_name: userName, text });
    if (error) {
      setUpdates(u => u.filter(x => x.id !== temp.id));
      setNewUpdate(text);
      toast('Could not save update — check your connection and try again.', 'error');
      return;
    }
    supabase.from('activity_log').insert({ room_id: room.id, claim_id: claimId, user_id: session.user.id, user_name: userName, action: 'update_added', detail: `added update on ${claim?.car || 'claim'}: "${text.slice(0, 60)}"` }).then(() => {});
    supabase.from('claims').update({ updated_at: new Date().toISOString() }).eq('id', claimId).then(() => {});
  }

  async function changeStatus(newStatus) {
    if (room.role !== 'admin') return;
    const userName = session.user.user_metadata?.full_name || session.user.email;
    const oldStatus = claim.status;
    if (newStatus === oldStatus) return;
    // Optimistic: flip the stage immediately, revert only if the write fails
    mutateClaim(c => ({ ...c, status: newStatus, updated_at: new Date().toISOString() }));
    const { error } = await supabase.from('claims').update({ status: newStatus, updated_at: new Date().toISOString() }).eq('id', claimId);
    if (error) {
      mutateClaim(c => ({ ...c, status: oldStatus }));
      toast('Could not change the stage — check your connection and try again.', 'error');
      return;
    }
    supabase.from('activity_log').insert({ room_id: room.id, claim_id: claimId, user_id: session.user.id, user_name: userName, action: 'status_change', detail: `moved ${claim?.car || 'claim'} from "${oldStatus}" → "${newStatus}"` }).then(() => {});
  }

  async function saveEdit() {
    setSaveError('');
    const userName = session.user.user_metadata?.full_name || session.user.email;
    const oldStatus = claim.status;
    const snapshot = claim;
    const { car, plate, color, vin, client_name, client_phone, insurer, claim_num, adjuster, adjuster_phone, adjuster_email, car_in, car_out, notes, status } = editForm;
    const payload = { car, plate, color, vin, client_name, client_phone, insurer, claim_num, adjuster, adjuster_phone, adjuster_email, car_in: car_in || null, car_out: car_out || null, notes, status, updated_at: new Date().toISOString() };
    // Optimistic: apply the edits and close edit mode immediately
    mutateClaim(c => ({ ...c, ...payload }));
    setEditing(false);
    const { error } = await supabase.from('claims').update(payload).eq('id', claimId);
    if (error) {
      setClaim(snapshot);
      setEditing(true);
      setSaveError('Save failed: ' + error.message);
      return;
    }
    // Log a stage change here too, so the "stuck in stage" timer stays accurate
    if (status && status !== oldStatus) {
      supabase.from('activity_log').insert({ room_id: room.id, claim_id: claimId, user_id: session.user.id, user_name: userName, action: 'status_change', detail: `moved ${car || 'claim'} from "${oldStatus}" → "${status}"` }).then(() => {});
    }
    supabase.from('activity_log').insert({ room_id: room.id, claim_id: claimId, user_id: session.user.id, user_name: userName, action: 'claim_edited', detail: `edited claim details for ${car || 'vehicle'}` }).then(() => {});
  }

  async function deleteClaim() {
    // Optimistic: close and remove the card immediately, delete in background
    if (onDeleted) onDeleted(claimId); else onClose();
    const { error } = await supabase.from('claims').delete().eq('id', claimId);
    if (error) toast('Could not delete the claim — it will reappear on refresh.', 'error');
  }

  function setField(f, v) { setEditForm(ef => ({ ...ef, [f]: v })); }

  // Warn before closing while the claim is being edited (unsaved changes)
  function tryClose() {
    if (editing && !window.confirm('Discard unsaved edits?')) return;
    onClose();
  }

  function printClaim() {
    const fmt = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
    const parts = claim.parts || [];
    const tags = claim.tags || [];
    const partsHtml = parts.length ? parts.map(p => `<tr><td>${p.name}</td><td style="text-align:center">${p.arrived ? '✓ Arrived' : 'Pending'}</td></tr>`).join('') : '<tr><td colspan="2" style="color:#888">No parts</td></tr>';
    const tagsHtml = tags.length ? tags.map(t => `<span style="display:inline-block;padding:3px 10px;border-radius:20px;border:1px solid #ccc;font-size:12px;margin:2px">${t.label || t.type}</span>`).join('') : '—';
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${claim.car || 'Claim'} — ${claim.plate || ''}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #1a1a1a; background: #fff; padding: 48px 56px; max-width: 800px; margin: 0 auto; }

  /* Header bar */
  .header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 20px; border-bottom: 3px solid #1a1a1a; margin-bottom: 28px; }
  .shop-name { font-size: 11px; font-weight: 700; letter-spacing: 3px; text-transform: uppercase; color: #888; margin-bottom: 6px; }
  .vehicle { font-size: 30px; font-weight: 800; letter-spacing: -0.5px; line-height: 1; }
  .vehicle-sub { margin-top: 8px; display: flex; align-items: center; gap: 10px; }
  .plate { font-family: monospace; font-size: 13px; font-weight: 700; letter-spacing: 2.5px; background: #f0f0f0; border: 1.5px solid #ccc; border-radius: 4px; padding: 3px 12px; }
  .color-badge { font-size: 13px; color: #666; }
  .vin { font-family: monospace; font-size: 11px; color: #bbb; margin-top: 5px; letter-spacing: 1px; }

  /* Logo top right */
  .logo-wrap { text-align: right; }
  .logo-wrap img { max-height: 64px; max-width: 160px; object-fit: contain; }

  /* Sections */
  .sections { display: grid; grid-template-columns: 1fr 1fr; gap: 0; }
  .section { padding: 22px 0; border-bottom: 1px solid #e8e8e8; }
  .section:nth-child(odd) { padding-right: 32px; border-right: 1px solid #e8e8e8; }
  .section:nth-child(even) { padding-left: 32px; }
  .section-title { font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: #aaa; margin-bottom: 14px; }
  .row { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 10px; }
  .row:last-child { margin-bottom: 0; }
  .row-label { font-size: 11px; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase; color: #999; }
  .row-value { font-size: 14px; font-weight: 500; color: #1a1a1a; text-align: right; max-width: 60%; }

  /* Notes */
  .notes-section { padding: 22px 0; border-bottom: 1px solid #e8e8e8; }
  .notes-body { font-size: 13px; line-height: 1.7; color: #444; white-space: pre-wrap; margin-top: 10px; }

  /* Footer */
  .footer { display: flex; justify-content: space-between; margin-top: 28px; font-size: 11px; color: #bbb; letter-spacing: 0.5px; }

  @media print { body { padding: 32px 40px; } }
</style></head><body>

  <div class="header">
    <div>
      <div class="shop-name">${room.shop_name || room.name || 'ShopTrack'}</div>
      <div class="vehicle">${claim.car || 'Unknown Vehicle'}</div>
      <div class="vehicle-sub">
        ${claim.plate ? `<span class="plate">${claim.plate}</span>` : ''}
        ${claim.color ? `<span class="color-badge">${claim.color}</span>` : ''}
      </div>
      ${claim.vin ? `<div class="vin">${claim.vin}</div>` : ''}
    </div>
    ${room.logo_url ? `<div class="logo-wrap"><img src="${room.logo_url}" alt="Logo" /></div>` : ''}
  </div>

  <div class="sections">
    <div class="section">
      <div class="section-title">Client</div>
      <div class="row"><span class="row-label">Name</span><span class="row-value">${claim.client_name || '—'}</span></div>
      <div class="row"><span class="row-label">Phone</span><span class="row-value">${claim.client_phone || '—'}</span></div>
      <div class="row"><span class="row-label">Car In</span><span class="row-value">${fmt(claim.car_in)}</span></div>
      <div class="row"><span class="row-label">Car Out</span><span class="row-value">${fmt(claim.car_out)}</span></div>
    </div>
    <div class="section">
      <div class="section-title">Insurance</div>
      <div class="row"><span class="row-label">Insurer</span><span class="row-value">${claim.insurer || '—'}</span></div>
      <div class="row"><span class="row-label">Claim #</span><span class="row-value">${claim.claim_num || '—'}</span></div>
      <div class="row"><span class="row-label">Adjuster</span><span class="row-value">${claim.adjuster || '—'}</span></div>
    </div>
  </div>

  ${claim.notes ? `<div class="notes-section"><div class="section-title">Notes</div><div class="notes-body">${claim.notes}</div></div>` : ''}


</body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 400);
  }

  const progress = claim ? stageProgress(claim.status) : 0;
  const color = claim ? (STAGE_COLORS[claim.status] || 'var(--orange)') : 'var(--orange)';
  const special = claim ? specialStageTheme(claim.status) : null;
  const tl = !!special; // "tl" now means "is a special tinted stage" (Total Loss or SIU)
  const tlBg = 'var(--sp-bg)';
  const tlBg2 = 'var(--sp-bg2)';
  const tlBg3 = 'var(--sp-bg3)';
  const tlBorder = 'var(--sp-border)';
  const tabs = ['info', 'updates', 'tasks', 'parts', 'tags', 'invoice', 'files'];

  // When the claim entered its current stage = latest status_change log (updates is sorted desc)
  const stageSince = updates.find(u => u._type === 'log' && u.action === 'status_change')?.created_at || claim?.created_at;
  const warnings = claim ? getClaimWarnings(claim, stageSince) : [];

  const modalMaxW = mobile ? '100%' : 'min(1100px, 96vw)';

  return (
    <div
      className="modal-overlay"
      style={{
        background: 'rgba(5,5,6,0.88)', zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        touchAction: 'none',
      }}
      onClick={e => e.target === e.currentTarget && tryClose()}
    >
      <div className={`modal-panel${special ? ' special-modal ' + special.cls : ''}`}
      style={{
        background: tl ? tlBg : 'var(--bg2)',
        border: `1px solid ${tl ? 'var(--sp-accent)' : 'var(--border)'}`,
        maxWidth: modalMaxW,
        height: mobile ? '100%' : 'min(860px, 94vh)',
      }}>
        {loading || !claim ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
            <div className="spinner" style={{ width: 36, height: 36 }} />
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={{ padding: mobile ? '16px 14px 12px' : '18px 24px 14px', boxShadow: 'inset 0 -9px 9px -9px rgba(0,0,0,0.6)', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: mobile ? 22 : 28, fontWeight: 700, lineHeight: 1.1 }}>
                    {claim.car || 'Unknown Vehicle'}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    {roomName && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: mobile ? 10.5 : 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text2)', background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: 20, padding: '3px 9px' }}><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/></svg>{roomName}</span>}
                    {claim.plate && <span style={{ fontFamily: 'var(--font-mono)', fontSize: mobile ? 11 : 12, fontWeight: 700, letterSpacing: '1.5px', background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: 5, padding: '3px 8px', color: 'var(--text2)' }}>{claim.plate}</span>}
                    {claim.color && <span style={{ fontSize: 13, color: 'var(--text3)' }}>{claim.color}</span>}
                    {claim.client_name && <span style={{ fontSize: 13, color: 'var(--text3)' }}>· {claim.client_name}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
                  {room.role === 'admin' && !editing && (
                    <button className="btn btn-secondary" onClick={() => { setEditing(true); setSaveError(''); }} style={{ fontSize: 13, padding: '7px 14px', background: tl ? tlBg2 : undefined, border: tl ? `1px solid ${tlBorder}` : 'none' }}>Edit</button>
                  )}
                  <button onClick={printClaim} className="raise-3d" style={{ background: tl ? tlBg2 : 'var(--bg3)', border: tl ? `1px solid ${tlBorder}` : 'none', color: 'var(--text2)', borderRadius: 8, padding: '7px 12px', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>🖨 Print</button>
                  <button onClick={tryClose} className="raise-3d" style={{ background: tl ? tlBg2 : 'var(--bg3)', border: tl ? `1px solid ${tlBorder}` : 'none', color: 'var(--text2)', borderRadius: '50%', width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
</button>
                </div>
              </div>

              {/* Progress bar */}
              <div style={{ height: 4, background: tl ? tlBg3 : 'var(--bg4)', borderRadius: 2, overflow: 'hidden', marginBottom: 12 }}>
                <div style={{ height: '100%', width: `${progress}%`, background: color, borderRadius: 2, transition: 'width 0.3s' }} />
              </div>

              {/* Stage picker — dropdown on mobile, grid on desktop */}
              {mobile ? (
                <div style={{ position: 'relative' }}>
                  <button onClick={() => room.role === 'admin' && setStageOpen(v => !v)} className="raise-3d" style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, width: '100%',
                    padding: '12px 14px', borderRadius: 8,
                    background: `${color}22`, border: `1px solid ${color}`,
                    cursor: room.role === 'admin' ? 'pointer' : 'default',
                  }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                      <span style={{ width: 9, height: 9, borderRadius: '50%', background: color, flexShrink: 0 }} />
                      <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color }}>{claim.status}</span>
                    </span>
                    {room.role === 'admin' && (
                      <svg width="14" height="14" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0, transform: stageOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}><path d="M3 4.5L6 7.5L9 4.5" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    )}
                  </button>
                  {stageOpen && (
                    <>
                      <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onClick={() => setStageOpen(false)} />
                      <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 20, background: tl ? tlBg2 : 'var(--bg2)', border: `1px solid ${tl ? tlBorder : 'var(--border)'}`, borderRadius: 10, overflow: 'hidden', boxShadow: '0 10px 32px rgba(0,0,0,0.55)', maxHeight: 340, overflowY: 'auto' }}>
                        {STAGES.map((s, i) => {
                          const sc = STAGE_COLORS[s] || 'var(--orange)';
                          const isCurrent = claim.status === s;
                          return (
                            <button key={s} onClick={() => { changeStatus(s); setStageOpen(false); }} style={{
                              display: 'flex', alignItems: 'center', gap: 11, width: '100%', textAlign: 'left',
                              padding: '13px 14px', background: isCurrent ? `${sc}18` : 'transparent',
                              border: 'none', borderBottom: i < STAGES.length - 1 ? `1px solid ${tl ? tlBorder : 'var(--border)'}` : 'none', cursor: 'pointer',
                            }}>
                              <span style={{ width: 9, height: 9, borderRadius: '50%', background: sc, flexShrink: 0 }} />
                              <span style={{ flex: 1, fontSize: 12, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: isCurrent ? sc : 'var(--text)' }}>{s}</span>
                              {isCurrent && <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7.5L5.5 11L12 3.5" stroke={sc} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '9px 6px', flexWrap: 'wrap' }}>
                  {STAGES.map(s => {
                    const sc = STAGE_COLORS[s] || 'var(--orange)';
                    const isCurrent = claim.status === s;
                    return (
                      <button key={s} onClick={() => changeStatus(s)} className={`pipe-3d${isCurrent ? ' pipe-3d-in' : ''}`} style={{
                        '--pipe-glow': `${sc}90`,
                        padding: '11px 18px', borderRadius: 8,
                        background: isCurrent ? `${sc}22` : tl ? tlBg2 : 'var(--bg3)',
                        border: tl ? `1px solid ${tlBorder}` : 'none',
                        fontSize: 12, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase',
                        color: isCurrent ? sc : 'var(--text)',
                        cursor: room.role === 'admin' ? 'pointer' : 'default',
                        whiteSpace: 'nowrap',
                      }}>{s}</button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Tabs */}
            <div className="tab-scroll" style={{ display: 'flex', boxShadow: 'inset 0 -9px 9px -9px rgba(0,0,0,0.6)', flexShrink: 0, overflowX: 'auto', scrollbarWidth: 'none', background: tl ? tlBg : undefined }}>
              {tabs.map(t => (
                <button key={t} onClick={() => { setActiveTab(t); if (t !== 'info') setEditing(false); }} style={{
                  padding: mobile ? '10px 10px' : '13px 14px',
                  border: 'none', borderBottom: `2px solid ${activeTab === t ? 'var(--orange)' : 'transparent'}`,
                  fontFamily: 'var(--font-display)', fontSize: mobile ? 11 : 13, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase',
                  color: activeTab === t ? 'var(--orange)' : 'var(--text3)',
                  background: 'transparent', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, transition: 'color 0.15s',
                }}>
                  {t === 'updates' ? `Updates (${updates.length})` : t === 'parts' ? `Parts (${(claim.parts || []).length})` : t}
                </button>
              ))}
            </div>

            {/* Tab body */}
            <div className="modal-scroll" style={{ flex: 1, touchAction: 'pan-y', background: tl ? tlBg : undefined }}>

              {/* ── INFO TAB — full overview ── */}
              {activeTab === 'info' && (
                <div style={{ padding: mobile ? '14px 14px' : '20px 24px', display: 'flex', flexDirection: 'column', gap: 18 }}>
                  {editing ? (
                    <>
                      <EditForm form={editForm} setField={setField} onSave={saveEdit} onCancel={() => { setEditing(false); setSaveError(''); setEditForm(claim); }} saving={saving} tl={tl} />
                      {saveError && <div style={{ color: 'var(--red)', fontSize: 13, padding: '8px 12px', background: 'rgba(239,68,68,0.1)', borderRadius: 8 }}>{saveError}</div>}
                      {room.role === 'admin' && (
                        <div>
                          {!showDelete
                            ? <button className="btn btn-danger" style={{ width: '100%', padding: 12 }} onClick={() => setShowDelete(true)}>Delete Claim</button>
                            : <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: 14 }}>
                                <div style={{ fontSize: 14, marginBottom: 12 }}>Delete this claim permanently?</div>
                                <div style={{ display: 'flex', gap: 8 }}>
                                  <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowDelete(false)}>Cancel</button>
                                  <button className="btn btn-danger" style={{ flex: 1 }} onClick={deleteClaim}>Yes, Delete</button>
                                </div>
                              </div>
                          }
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Warnings */}
                      {warnings.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {warnings.map(w => {
                            const c = w.severity === 'high' ? '239,68,68' : '234,179,8';
                            return (
                              <div key={w.type} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 13px', borderRadius: 10, background: `rgba(${c},0.1)`, border: `1px solid rgba(${c},0.35)` }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={`rgb(${c})`} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M12 3 2 20h20L12 3z"/><line x1="12" y1="10" x2="12" y2="14"/><line x1="12" y1="17.5" x2="12.01" y2="17.5"/></svg>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontSize: 13, fontWeight: 700, color: `rgb(${c})` }}>{w.label}</div>
                                  <div style={{ fontSize: 12, color: 'var(--text2)' }}>{w.detail}</div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Claim details */}
                      <InfoView claim={claim} mobile={mobile} tl={tl} />

                      {/* Tags preview */}
                      {(claim.tags || []).length > 0 && (
                        <InfoSection title="Tags">
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {(claim.tags || []).map(tag => (
                              <span key={tag.id} className={`tag-chip ${tag.type}`}>{tag.type === 'tow' ? <IcoTow size={12} /> : tag.type === 'rental' ? <IcoRental size={12} /> : null}{tag.label}</span>
                            ))}
                          </div>
                        </InfoSection>
                      )}

                      {/* Parts summary */}
                      {(claim.parts || []).length > 0 && (
                        <InfoSection title="Parts" action="Manage" onAction={() => setActiveTab('parts')}>
                          <PartsOverview parts={claim.parts} />
                        </InfoSection>
                      )}

                      {/* Recent updates preview — manual updates only */}
                      {updates.filter(u => u._type === 'update').length > 0 && (
                        <InfoSection title="Recent Updates" action={updates.filter(u => u._type === 'update').length > 3 ? `See all (${updates.filter(u => u._type === 'update').length})` : null} onAction={() => setActiveTab('updates')}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {updates.filter(u => u._type === 'update').slice(0, 3).map(u => (
                              <div key={u.id} style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', borderLeft: '3px solid var(--orange)' }}>
                                <div style={{ fontSize: 13, lineHeight: 1.5 }}>{u.text}</div>
                                <div style={{ fontSize: 10.5, color: 'var(--text3)', marginTop: 4 }}>{u.user_name} · {timeAgo(u.created_at)}</div>
                              </div>
                            ))}
                          </div>
                        </InfoSection>
                      )}

                      {/* Files preview */}
                      {files.length > 0 && (
                        <InfoSection title="Files" action="See all" onAction={() => setActiveTab('files')}>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {files.filter(f => isImage(f.name)).slice(0, 4).map(f => (
                              <a key={f.name} href={f.url} target="_blank" rel="noopener noreferrer"
                                style={{ width: 72, height: 72, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)', background: 'var(--bg3)', display: 'block', flexShrink: 0 }}>
                                <img src={f.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              </a>
                            ))}
                            {files.filter(f => !isImage(f.name)).map(f => (
                              <a key={f.name} href={f.url} target="_blank" rel="noopener noreferrer"
                                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, color: 'var(--text)', textDecoration: 'none', flexShrink: 0 }}>
                                <span style={{ display: 'flex', color: 'var(--text2)' }}><IcoFile size={14} /></span>{f.name.replace(/^\d+-/, '').slice(0, 20)}
                              </a>
                            ))}
                          </div>
                        </InfoSection>
                      )}

                      {/* Rental summary */}
                      {rental && rental.daily_rate && rental.start_date && rental.end_date && (
                        <InfoSection title="Rental" action="Edit" onAction={() => setActiveTab('rental')}>
                          <RentalOverview rental={rental} />
                        </InfoSection>
                      )}

                      {/* Add update quick-bar */}
                      <InfoSection title="Add Update">
                        <form onSubmit={addUpdate} style={{ display: 'flex', gap: 8 }}>
                          <input value={newUpdate} onChange={e => setNewUpdate(e.target.value)} placeholder="Type an update and hit send..." style={{ flex: 1, background: tl ? 'var(--sp-bg2)' : undefined, borderColor: tl ? 'var(--sp-border)' : undefined }} />
                          <button type="submit" disabled={addingUpdate} className="raise-3d-orange" style={{ background: 'var(--orange)', color: '#16100b', borderRadius: 10, padding: '0 16px', fontSize: 18, fontWeight: 800, border: 'none', cursor: 'pointer', flexShrink: 0 }}>
                            {addingUpdate ? <div className="spinner" style={{ width: 16, height: 16 }} /> : '↑'}
                          </button>
                        </form>
                      </InfoSection>
                    </>
                  )}
                </div>
              )}

              {/* ── UPDATES TAB ── */}
              {activeTab === 'updates' && (
                <div style={{ padding: mobile ? 14 : 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <form onSubmit={addUpdate} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <textarea
                      value={newUpdate}
                      onChange={e => setNewUpdate(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addUpdate(e); } }}
                      placeholder="Add an update... (Enter to send, Shift+Enter for new line)"
                      rows={9}
                      style={{ resize: 'none', background: tl ? 'var(--sp-bg2)' : undefined, borderColor: tl ? 'var(--sp-border)' : undefined }}
                    />
                    <button type="submit" disabled={addingUpdate || !newUpdate.trim()} className="raise-3d-orange" style={{ background: 'var(--orange)', color: '#16100b', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 700, border: 'none', cursor: newUpdate.trim() ? 'pointer' : 'default', alignSelf: 'flex-end', opacity: newUpdate.trim() ? 1 : 0.4, transition: 'opacity 0.15s', display: 'flex', alignItems: 'center', gap: 6 }}>
                      {addingUpdate ? <div className="spinner" style={{ width: 14, height: 14 }} /> : 'Post Update'}
                    </button>
                  </form>

                  {emailThreads.length > 0 && (
                    <div>
                      <SLabel>Email Threads</SLabel>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                        {emailThreads.map(t => (
                          <EmailThreadRow key={t.id} thread={t} expanded={expandedThreadId === t.id} onToggle={() => setExpandedThreadId(expandedThreadId === t.id ? null : t.id)} />
                        ))}
                      </div>
                    </div>
                  )}

                  {updates.length === 0
                    ? <div className="empty-state"><div className="icon">💬</div><div className="title">No updates yet</div></div>
                    : updates.map(u => {
                        const isLog = u._type === 'log';
                        const accent = isLog
                          ? (u.action === 'part_arrived' ? 'var(--green)' : u.action === 'tag_added' ? 'var(--blue)' : 'var(--text3)')
                          : 'var(--orange)';
                        return (
                          <div key={u.id} style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 12, padding: '11px 13px', borderLeft: `3px solid ${accent}` }}>
                            <div style={{ fontSize: mobile ? 13 : 14, lineHeight: 1.55, color: isLog ? 'var(--text2)' : 'var(--text)' }}>{isLog ? u.detail : u.text}</div>
                            <div style={{ fontSize: 10.5, color: 'var(--text3)', marginTop: 5 }}>{u.user_name} · {timeAgo(u.created_at)}</div>
                          </div>
                        );
                      })
                  }
                </div>
              )}

              {activeTab === 'tasks' && <TasksTab claim={claim} session={session} room={room} tl={tl} />}
              {activeTab === 'parts' && <PartsTab claim={claim} session={session} room={room} onRefresh={loadClaim} onLocal={mutateClaim} tl={tl} />}
              {activeTab === 'tags' && <TagsTab claim={claim} session={session} room={room} onRefresh={loadClaim} onLocal={mutateClaim} tl={tl} />}
              {activeTab === 'invoice' && <InvoiceTab claim={claim} room={room} tl={tl} />}
              {activeTab === 'files' && <FilesTab claim={claim} session={session} room={room} onRefresh={loadFiles} />}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── EMAIL THREAD ROW ──────────────────────────────────────────────────────────
function EmailThreadRow({ thread, expanded, onToggle }) {
  const messages = thread.email_messages || [];
  const last = messages[messages.length - 1];
  function fmtTime(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  }
  return (
    <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: '11px 14px' }}>
      <div onClick={onToggle} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{thread.subject || '(no subject)'}</div>
          {last && <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{last.preview_text}</div>}
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{fmtTime(thread.last_message_at)}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{messages.length} msg{messages.length !== 1 ? 's' : ''}</div>
        </div>
      </div>
      {expanded && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {messages.map(m => (
            <div key={m.id} style={{ fontSize: 12.5 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontWeight: 600, color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.sender}</span>
                <span style={{ color: 'var(--text3)', flexShrink: 0 }}>{fmtTime(m.received_at)}</span>
              </div>
              <div style={{ color: 'var(--text3)', marginTop: 2 }}>{m.preview_text}</div>
            </div>
          ))}
          {thread.gmail_thread_id && (
            <a href={`https://mail.google.com/mail/u/0/#inbox/${thread.gmail_thread_id}`} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 12, color: 'var(--blue)', textDecoration: 'none' }}>
              Open in Gmail →
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// ── INFO SECTION WRAPPER ──────────────────────────────────────────────────────
function InfoSection({ title, children, action, onAction }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <SLabel>{title}</SLabel>
        {action && <button onClick={onAction} style={{ background: 'none', border: 'none', color: 'var(--orange)', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}>{action} →</button>}
      </div>
      {children}
    </div>
  );
}

// ── PARTS OVERVIEW ────────────────────────────────────────────────────────────
function PartsOverview({ parts }) {
  const arrived = parts.filter(p => p.arrived).length;
  const total = parts.length;
  const color = arrived === total ? 'var(--green)' : arrived > 0 ? 'var(--yellow)' : 'var(--red)';
  return (
    <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 13, color: 'var(--text2)' }}>Parts Status</span>
        <span style={{ fontSize: 13, fontWeight: 700, color }}>{arrived === total ? 'All In' : arrived > 0 ? 'Partial' : 'Waiting'} ({arrived}/{total})</span>
      </div>
      <div style={{ display: 'flex', gap: 3 }}>
        {parts.map(p => <div key={p.id} style={{ flex: 1, height: 4, borderRadius: 2, background: p.arrived ? 'var(--green)' : 'var(--bg4)' }} />)}
      </div>
      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {parts.slice(0, 4).map(p => (
          <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
            <div style={{ width: 14, height: 14, borderRadius: 3, border: `1.5px solid ${p.arrived ? 'var(--green)' : 'var(--border)'}`, background: p.arrived ? 'var(--green)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 10, color: 'white', fontWeight: 700 }}>{p.arrived ? '✓' : ''}</div>
            <span style={{ color: p.arrived ? 'var(--text3)' : 'var(--text)', textDecoration: p.arrived ? 'line-through' : 'none' }}>{p.name}</span>
          </div>
        ))}
        {parts.length > 4 && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>+{parts.length - 4} more</div>}
      </div>
    </div>
  );
}

// ── RENTAL OVERVIEW ───────────────────────────────────────────────────────────
function RentalOverview({ rental }) {
  const days = rental.start_date && rental.end_date
    ? Math.max(0, Math.ceil((new Date(rental.end_date) - new Date(rental.start_date)) / 86400000))
    : 0;
  const total = days * (parseFloat(rental.daily_rate) || 0);
  return (
    <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div>
        <div style={{ fontSize: 13, color: 'var(--text2)' }}>{rental.company || 'Rental'}</div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{days} days · ${parseFloat(rental.daily_rate || 0).toFixed(2)}/day</div>
      </div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 20, fontWeight: 700, color: 'var(--green)' }}>${total.toFixed(2)}</div>
    </div>
  );
}

// ── FILES TAB ─────────────────────────────────────────────────────────────────
function FilesTab({ claim, session, room, onRefresh }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [loadingFiles, setLoadingFiles] = useState(true);
  const BUCKET = 'claim-files';

  useEffect(() => { loadFiles(); }, [claim.id]);

  async function loadFiles() {
    setLoadingFiles(true);
    const { data, error } = await supabase.storage.from(BUCKET).list(`${room.id}/${claim.id}`, { sortBy: { column: 'created_at', order: 'desc' } });
    if (error || !data) { setFiles([]); setLoadingFiles(false); return; }
    const withUrls = await Promise.all(data.map(async f => {
      const path = `${room.id}/${claim.id}/${f.name}`;
      const { data: urlData } = await supabase.storage.from(BUCKET).createSignedUrl(path, 3600);
      return { ...f, url: urlData?.signedUrl, path };
    }));
    setFiles(withUrls);
    setLoadingFiles(false);
  }

  async function handleUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadError('');
    const safeName = `${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
    const path = `${room.id}/${claim.id}/${safeName}`;
    const { error } = await supabase.storage.from(BUCKET).upload(path, file, { cacheControl: '3600', upsert: false });
    if (error) { setUploadError('Upload failed: ' + error.message); }
    else {
      await supabase.from('activity_log').insert({ room_id: room.id, claim_id: claim.id, user_id: session.user.id, user_name: session.user.user_metadata?.full_name || session.user.email, action: 'file_uploaded', detail: `uploaded "${file.name}" to ${claim.car || 'claim'}` });
      await supabase.from('claims').update({ updated_at: new Date().toISOString() }).eq('id', claim.id);
      loadFiles();
      if (onRefresh) onRefresh();
    }
    setUploading(false);
    e.target.value = '';
  }

  async function deleteFile(filePath, fileName) {
    if (!window.confirm(`Delete "${fileName}"?`)) return;
    const { error } = await supabase.storage.from(BUCKET).remove([filePath]);
    if (error) { toast('Delete failed: ' + error.message, 'error'); return; }
    loadFiles();
  }

  const cleanName = n => n.replace(/^\d+-/, '');
  const formatSize = b => !b ? '' : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`;
  const imageFiles = files.filter(f => isImage(f.name));
  const docFiles = files.filter(f => !isImage(f.name));

  return (
    <div style={{ padding: 16 }}>
      <label className="raise-3d" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '14px 20px', marginBottom: 16, background: uploading ? 'var(--bg3)' : 'var(--orange-dim)', border: `1.5px dashed ${uploading ? 'var(--border)' : 'var(--orange)'}`, borderRadius: 12, cursor: uploading ? 'default' : 'pointer' }}>
        {uploading ? <><div className="spinner" style={{ width: 18, height: 18 }} /><span style={{ fontSize: 14, color: 'var(--text3)' }}>Uploading...</span></> : <><span style={{ display: 'flex', color: 'var(--orange)' }}><IcoUpload size={20} /></span><span style={{ fontSize: 14, fontWeight: 600, color: 'var(--orange)' }}>Upload Photo or File</span><span style={{ fontSize: 12, color: 'var(--text3)' }}>Images, PDFs, estimates</span></>}
        <input type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" onChange={handleUpload} disabled={uploading} style={{ display: 'none' }} />
      </label>
      {uploadError && <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 12, padding: '8px 12px', background: 'rgba(239,68,68,0.1)', borderRadius: 8 }}>{uploadError}</div>}
      {loadingFiles ? <div className="empty-state"><div className="spinner" /></div>
        : files.length === 0 ? <div className="empty-state"><div className="icon" style={{ color: 'var(--text3)' }}><IcoFolder size={40} /></div><div className="title">No files yet</div><div className="sub">Upload photos, estimates, or documents</div></div>
        : <>
          {imageFiles.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <SLabel>Photos</SLabel>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 8 }}>
                {imageFiles.map(f => (
                  <div key={f.name} style={{ position: 'relative', aspectRatio: '1', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)', background: 'var(--bg3)' }}>
                    <a href={f.url} target="_blank" rel="noopener noreferrer"><img src={f.url} alt={cleanName(f.name)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></a>
                    {room.role === 'admin' && <button onClick={() => deleteFile(f.path, cleanName(f.name))} style={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: '50%', background: 'rgba(0,0,0,0.7)', color: 'white', border: 'none', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {docFiles.length > 0 && (
            <div>
              <SLabel>Documents</SLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {docFiles.map(f => (
                  <div key={f.name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10 }}>
                    <span style={{ display: 'flex', flexShrink: 0, color: 'var(--text2)' }}><IcoFile size={18} /></span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <a href={f.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', textDecoration: 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cleanName(f.name)}</a>
                      {f.metadata?.size && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{formatSize(f.metadata.size)}</div>}
                    </div>
                    {room.role === 'admin' && <button onClick={() => deleteFile(f.path, cleanName(f.name))} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 18, padding: '0 4px', flexShrink: 0 }}>×</button>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      }
    </div>
  );
}

// ── HELPERS ───────────────────────────────────────────────────────────────────
function isImage(name) { return /\.(jpg|jpeg|png|gif|webp|heic)$/i.test(name); }

function SLabel({ children }) {
  return <div style={{ fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 700, color: 'var(--orange)', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 8 }}>{children}</div>;
}

function IBox({ label, value, mono, span2, tl, always, href }) {
  if (!value && !always) return null;
  // Phones/emails become tap-to-call / tap-to-email links
  const inner = value && href
    ? <a href={href} style={{ color: 'var(--green)', textDecoration: 'none' }}>{value}</a>
    : (value || '—');
  return (
    <div style={{ background: tl ? 'var(--sp-bg2)' : 'var(--bg3)', border: `1px solid ${tl ? 'var(--sp-border)' : 'var(--border)'}`, borderRadius: 12, padding: '11px 12px', gridColumn: span2 ? 'span 2' : undefined }}>
      <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 4, letterSpacing: '0.5px', textTransform: 'uppercase', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 600, fontFamily: mono ? 'var(--font-mono)' : undefined, wordBreak: 'break-word', color: value ? undefined : 'var(--text3)' }}>{inner}</div>
    </div>
  );
}

function InfoView({ claim, tl }) {
  const tlBox = tl ? { background: 'var(--sp-bg2)', border: '1px solid var(--sp-border)' } : { background: 'var(--bg3)', border: '1px solid var(--border)' };
  function fmtDate(d) { if (!d) return null; const p = d.split('-'); return `${p[1]}/${p[2]}/${p[0]}`; }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {claim.vin && (
        <div>
          <SLabel>VIN</SLabel>
          <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 3 }}>
            {claim.vin.split('').map((ch, i) => (
              <div key={i} style={{ flex: '1 1 0', minWidth: 0, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', background: tl ? 'var(--sp-bg2)' : 'var(--bg4)', border: `1px solid ${tl ? 'var(--sp-border)' : 'var(--border)'}`, borderRadius: 4, fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 700, color: 'var(--text)', letterSpacing: 0 }}>{ch}</div>
            ))}
          </div>
        </div>
      )}
      <div><SLabel>Client</SLabel>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <IBox label="Name" value={claim.client_name} tl={tl} />
          <IBox label="Phone" value={claim.client_phone} tl={tl} href={claim.client_phone ? `tel:${claim.client_phone}` : undefined} />
        </div>
      </div>
      <div><SLabel>Insurance</SLabel>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <IBox label="Company" value={claim.insurer} tl={tl} />
          <IBox label="Claim #" value={claim.claim_num} mono tl={tl} />
          <IBox label="Adjuster" value={claim.adjuster} span2 tl={tl} />
          <IBox label="Adjuster Phone" value={claim.adjuster_phone} tl={tl} always href={claim.adjuster_phone ? `tel:${claim.adjuster_phone}` : undefined} />
          <IBox label="Adjuster Email" value={claim.adjuster_email} tl={tl} always href={claim.adjuster_email ? `mailto:${claim.adjuster_email}` : undefined} />
        </div>
      </div>
      <div><SLabel>Dates</SLabel>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <IBox label="Car In" value={fmtDate(claim.car_in)} tl={tl} />
          <IBox label="Est. Car Out" value={fmtDate(claim.car_out)} tl={tl} />
        </div>
      </div>
      {claim.notes && (
        <div><SLabel>Notes</SLabel>
          <div style={{ ...tlBox, borderRadius: 12, padding: '11px 12px', fontSize: 13.5, lineHeight: 1.6 }}>{claim.notes}</div>
        </div>
      )}
    </div>
  );
}

function EditForm({ form, setField, onSave, onCancel, saving, tl }) {
  const tlInput = tl ? { background: 'var(--sp-bg2)', borderColor: 'var(--sp-border)' } : {};
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <SLabel>Vehicle</SLabel>
        <div className="field"><label className="label">Year, Make & Model</label><input value={form.car || ''} onChange={e => setField('car', e.target.value)} style={tlInput} /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="field"><label className="label">Plate</label><input value={form.plate || ''} onChange={e => setField('plate', e.target.value.toUpperCase())} style={{ fontFamily: 'var(--font-mono)', ...tlInput }} /></div>
          <div className="field"><label className="label">Color</label><input value={form.color || ''} onChange={e => setField('color', e.target.value)} style={tlInput} /></div>
        </div>
        <div className="field">
          <label className="label">VIN</label>
          <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 3 }}>
            {Array.from({ length: 17 }).map((_, i) => (
              <input
                key={i}
                value={(form.vin || '')[i] || ''}
                maxLength={1}
                onChange={e => {
                  const chars = (form.vin || '').split('');
                  chars[i] = e.target.value.toUpperCase();
                  setField('vin', chars.join(''));
                  if (e.target.value && e.target.nextSibling) e.target.nextSibling.focus();
                }}
                onKeyDown={e => {
                  if (e.key === 'Backspace' && !e.target.value && e.target.previousSibling) e.target.previousSibling.focus();
                }}
                className="vin-box"
                style={{ flex: '1 1 0', minWidth: 0, width: 'auto', height: 34, textAlign: 'center', padding: 0, fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 700, borderRadius: 4, background: tl ? 'var(--sp-bg2)' : 'var(--bg4)', border: `1px solid ${tl ? 'var(--sp-border)' : 'var(--border)'}` }}
              />
            ))}
          </div>
        </div>
      </div>
      <div>
        <SLabel>Client</SLabel>
        <div className="field"><label className="label">Name</label><input value={form.client_name || ''} onChange={e => setField('client_name', e.target.value)} style={tlInput} /></div>
        <div className="field"><label className="label">Phone</label><input value={form.client_phone || ''} onChange={e => setField('client_phone', e.target.value)} type="tel" style={tlInput} /></div>
      </div>
      <div>
        <SLabel>Insurance</SLabel>
        <div className="field"><label className="label">Company</label>
          <input value={form.insurer || ''} onChange={e => setField('insurer', e.target.value)} style={tlInput} list="insurer-suggestions-edit" />
          <datalist id="insurer-suggestions-edit">
            {INSURERS.map(ins => <option key={ins.name} value={ins.name} />)}
          </datalist>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="field"><label className="label">Claim #</label><input value={form.claim_num || ''} onChange={e => setField('claim_num', e.target.value)} style={{ fontFamily: 'var(--font-mono)', ...tlInput }} /></div>
          <div className="field"><label className="label">Adjuster</label><input value={form.adjuster || ''} onChange={e => setField('adjuster', e.target.value)} style={tlInput} /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="field"><label className="label">Adjuster Phone</label><input value={form.adjuster_phone || ''} onChange={e => setField('adjuster_phone', e.target.value)} type="tel" style={tlInput} /></div>
          <div className="field"><label className="label">Adjuster Email</label><input value={form.adjuster_email || ''} onChange={e => setField('adjuster_email', e.target.value)} type="email" style={tlInput} /></div>
        </div>
      </div>
      <div>
        <SLabel>Dates</SLabel>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="field"><label className="label">Car In</label><input type="date" value={form.car_in || ''} onChange={e => setField('car_in', e.target.value)} style={tlInput} /></div>
          <div className="field"><label className="label">Est. Car Out</label><input type="date" value={form.car_out || ''} onChange={e => setField('car_out', e.target.value)} style={tlInput} /></div>
        </div>
      </div>
      <div>
        <SLabel>Notes</SLabel>
        <div className="field"><textarea value={form.notes || ''} onChange={e => setField('notes', e.target.value)} rows={3} style={{ resize: 'vertical', ...tlInput }} /></div>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn btn-secondary" onClick={onCancel} style={{ flex: 1, padding: 13 }}>Cancel</button>
        <button className="btn btn-primary" onClick={onSave} disabled={saving} style={{ flex: 2, padding: 13, fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 800, textTransform: 'uppercase' }}>
          {saving ? <div className="spinner" /> : 'Save Claim'}
        </button>
      </div>
    </div>
  );
}
